// src/oura/components/newscan/ScanFlowFoot.tsx
//
// The sticky foot of the New scan page: Back on the left (or a short note),
// then a status line and the pills on the right, at most one secondary and
// one primary. Above them, the draft's last message in a polite live region,
// so "Changed in 3 questions" is said once without moving focus. A success
// message is only said (sr-only): the step already shows the outcome inline.
// Errors and notes stay visible. The status line ("2 decisions left") sits in
// its own always-mounted polite region, so a change in it is said too.
//
// `confirm` swaps the pills for the Drawer foot's confirm row: the sentence,
// the verb in blue and Cancel. Cancel takes focus, Esc cancels, and focus goes
// back to the pill that opened it. The paid Start uses it.
//
// Phone: the pills are 44px tall, the primary fills what is left of the row,
// the status line sits on its own row above, and the bar clears the home
// indicator (safe-area inset).
//
// Steps never render this: they declare a FootSpec with `foot.set(...)` and
// the page renders it.

import { ArrowLeftIcon } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef } from "react";
import { cn } from "@oura/lib/cn";
import { LATE_TEXT, PillButton, TYPE, useEscapeLayer } from "@oura/ui";
import { FOOT, FRAME } from "@oura/newscan/copy";
import type { FootSpec } from "@oura/newscan/flowSteps";

export interface ScanFlowFootMessage {
    tone: "success" | "error" | "info";
    text: string;
}

export interface ScanFlowFootProps {
    spec: FootSpec | null;
    /** `ctl.message`: said once through the live region. */
    message?: ScanFlowFootMessage | null;
    /** The two-panel Review (First AI check): the foot spans the wider page. */
    wide?: boolean;
    className?: string;
}

const PHONE_PILL = "max-sm:h-11 max-sm:px-4";

export function ScanFlowFoot({ spec, message, wide, className }: ScanFlowFootProps) {
    const confirm = spec?.confirm ?? null;
    const confirmOpen = Boolean(confirm);
    const confirmTextId = useId();
    const rowRef = useRef<HTMLDivElement>(null);
    const opener = useRef<HTMLElement | null>(null);

    // Cancel takes focus when the confirm row opens; the opener gets it back after.
    useLayoutEffect(() => {
        if (!confirmOpen) return;
        const active = document.activeElement;
        opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
        // The same node while the foot shows: the pills come back inside it.
        const row = rowRef.current;
        row?.querySelector<HTMLElement>("[data-foot-cancel]")?.focus();
        return () => {
            requestAnimationFrame(() => {
                const now = document.activeElement;
                if (now && now !== document.body && now.isConnected) return;
                const back = opener.current?.isConnected ? opener.current : row?.querySelector<HTMLElement>("[data-foot-primary]");
                back?.focus();
            });
        };
    }, [confirmOpen]);

    useEscapeLayer(() => {
        if (confirm && !confirm.busy) confirm.onCancel();
    }, confirmOpen);

    // A double click never lands on the next step's primary: for a moment
    // after the primary's words change, it takes no click. Nor for a moment after
    // the primary was pressed, since the next step's primary often has the same
    // words ("Continue") and a label change alone would let it through (Jack, #4286).
    const primaryLabel = spec?.primary?.label ?? null;
    const lastLabel = useRef<string | null>(primaryLabel);
    const coolUntil = useRef(0);
    const lastPrimaryClick = useRef<number | null>(null);
    useEffect(() => {
        if (lastLabel.current !== null && primaryLabel !== null && primaryLabel !== lastLabel.current) coolUntil.current = Date.now() + 600;
        lastLabel.current = primaryLabel;
    }, [primaryLabel]);
    const runPrimary = (onClick: () => void) => {
        const now = Date.now();
        if (now < coolUntil.current) return;
        if (lastPrimaryClick.current !== null && now - lastPrimaryClick.current < 600) return;
        lastPrimaryClick.current = now;
        onClick();
    };

    const back = spec?.back ?? null;
    const secondary = spec?.secondary ?? null;
    const primary = spec?.primary ?? null;

    return (
        <footer
            className={cn(
                "z-20 shrink-0 border-t border-gray-100 bg-white pt-3",
                "pb-[max(0.75rem,env(safe-area-inset-bottom))]",
                className,
            )}
        >
            <div className={cn("mx-auto w-full px-5", wide ? "max-w-none lg:px-8" : "max-w-[880px]")}>
                <div aria-live="polite">
                    {message && (
                        <p
                            className={cn(
                                TYPE.tc,
                                message.tone === "success" ? "sr-only" : "m-0 mb-2 break-words",
                                message.tone === "error" ? LATE_TEXT : "text-gray-600",
                            )}
                        >
                            {message.text}
                        </p>
                    )}
                </div>
                <div ref={rowRef}>
                    {confirm ? (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <p id={confirmTextId} className={cn(TYPE.tb, "m-0 min-w-0 flex-1 basis-60 break-words font-medium text-gray-900")}>
                                {confirm.text}
                            </p>
                            <div className="flex items-center gap-2 max-sm:w-full">
                                <PillButton data-foot-cancel="" disabled={confirm.busy} onClick={confirm.onCancel} className={PHONE_PILL}>
                                    {FOOT.cancel}
                                </PillButton>
                                <PillButton
                                    variant="primary"
                                    busy={confirm.busy}
                                    aria-describedby={confirmTextId}
                                    onClick={confirm.onConfirm}
                                    className={cn(PHONE_PILL, "max-sm:flex-1")}
                                >
                                    {confirm.verb || FOOT.confirmVerb}
                                </PillButton>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            {back ? (
                                <PillButton
                                    icon={ArrowLeftIcon}
                                    busy={back.busy}
                                    disabled={back.disabled}
                                    onClick={back.onClick}
                                    className={PHONE_PILL}
                                >
                                    {back.label ?? FRAME.back}
                                </PillButton>
                            ) : (
                                spec?.note && <div className={cn(TYPE.tc, "min-w-0 text-gray-500 max-sm:hidden")}>{spec.note}</div>
                            )}
                            {/* Always mounted, so a new status is announced; a static note is not live. */}
                            <div
                                aria-live="polite"
                                aria-atomic="true"
                                className={
                                    spec?.status
                                        ? cn(TYPE.tc, "min-w-0 tabular-nums text-gray-500 sm:ml-auto max-sm:order-first max-sm:basis-full")
                                        : "sr-only"
                                }
                            >
                                {spec?.status}
                            </div>
                            {!spec?.status && !back && spec?.note && (
                                <div className={cn(TYPE.tc, "min-w-0 text-gray-500 sm:hidden max-sm:order-first max-sm:basis-full")}>
                                    {spec.note}
                                </div>
                            )}
                            {/* Wraps on a phone: a long primary ("Start the first AI check") takes its own line, never a sideways scroll. */}
                            <div className={cn("flex min-w-0 flex-wrap items-center justify-end gap-2 max-sm:flex-1", !spec?.status && "ml-auto")}>
                                {secondary && (
                                    <PillButton
                                        icon={secondary.icon}
                                        busy={secondary.busy}
                                        disabled={secondary.disabled}
                                        onClick={secondary.onClick}
                                        className={PHONE_PILL}
                                    >
                                        {secondary.label}
                                    </PillButton>
                                )}
                                {primary && (
                                    <PillButton
                                        data-foot-primary=""
                                        variant="primary"
                                        icon={primary.icon}
                                        busy={primary.busy}
                                        disabled={primary.disabled}
                                        onClick={() => runPrimary(primary.onClick)}
                                        className={cn(PHONE_PILL, "max-sm:flex-1")}
                                    >
                                        {primary.label}
                                    </PillButton>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
}
